import {
  Button,
  ButtonDropdown,
  Message,
  Status,
  Table,
  Tag,
  Modal,
  InputArea,
  Icon,
} from '@bitcine/cinesend-theme/dist'
import { useSnackbar } from '../../hooks/use_snackbar'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { convertToDateTime } from '../../helpers/convert_date'
import useStore from '../../hooks/use_store'
import useAuth from '../../hooks/use_auth'
import axios from 'axios'
import { Fragment, useState } from 'react'
import capitalize from '../../helpers/capitalize'
import { v4 as uuid } from 'uuid'

const POLL_TIME = 5000 // 5 seconds

export default function Snapshots({ cycleID, hasErrors }) {
  const queryKey = [`snapshots?cycle_id=${cycleID}`]
  const { data, isLoading, error } = useQuery(queryKey, {
    refetchInterval: (data) => {
      const hasInProgress = data?.data?.some((snapshot) => !snapshot.is_completed && !snapshot.is_failed)
      return hasInProgress ? POLL_TIME : false
    },
  })
  const [modalOpen, setModalOpen] = useState(false)
  const [detailModalOpenForSnapshot, setDetailModalOpenForSnapshot] = useState(null)
  const snapshots = data?.data || []
  const { addMessage } = useSnackbar()
  const queryClient = useQueryClient()
  const { createModel, deleteModel } = useStore()
  const { user } = useAuth()
  const handleClick = () => {
    if (hasErrors) {
      setModalOpen(true)
    } else {
      generateSnapshot()
    }
  }
  const generateSnapshot = () =>
    createModel.mutate(
      {
        endpoint: 'snapshots',
        data: {
          cycle_id: cycleID,
        },
      },
      {
        onSuccess: ({ data }) => {
          // Since we're polling for progress, no need to invalidate the query
          // Instead we'll just shove the response in the cache and let the polling update it
          queryClient.setQueryData(queryKey, (oldData) => {
            if (!oldData?.data) {
              return {
                data: [data.snapshot],
              }
            }
            return {
              ...oldData,
              data: [data.snapshot, ...oldData.data],
            }
          })
        },
        onError: (error) => {
          addMessage(`Error triggering snapshot generation: ${error.response?.data?.message || error.message}`, 'error')
        },
      }
    )
  const deleteSnapshot = (snapshot) => {
    deleteModel.mutate(
      {
        endpoint: 'snapshots',
        id: snapshot.id,
      },
      { onSuccess: () => queryClient.invalidateQueries(queryKey) }
    )
  }
  const sendSnapshotToViasat = useMutation(
    ({ snapshotID, publish = null }) => {
      return axios.post(`snapshots/${snapshotID}/send-to-viasat`, null, {
        params: {
          publish,
        },
      })
    },
    {
      onSuccess: () => {
        addMessage('Snapshot sent to Viasat API successfully.')
        queryClient.invalidateQueries(queryKey)
      },
      onError: (error) => {
        addMessage(`Error sending snapshot to Viasat API: ${error.response?.data?.message || error.message}`, 'error')
        queryClient.invalidateQueries(queryKey)
      },
    }
  )

  const promoteSnapshotToViasat = useMutation(
    ({ snapshotID }) => {
      return axios.post(`snapshots/${snapshotID}/promote`)
    },
    {
      onSuccess: () => {
        addMessage('Snapshot "Promote" to Viasat API sent successfully.')
        queryClient.invalidateQueries(queryKey)
      },
      onError: (error) => {
        addMessage(`Error promoting snapshot to Viasat API: ${error.response?.data?.message || error.message}`, 'error')
        queryClient.invalidateQueries(queryKey)
      },
    }
  )

  const extractExternalErrors = (snapshot) => {
    let externalErrors = null
    const json = snapshot?.external_error ?? '<empty json>'
    try {
      externalErrors = JSON.parse(json)
      // if errors is double-nested, extract the lowest errors and re-format
      if (externalErrors?.errors?.[0]?.errors) {
        externalErrors.errors = externalErrors.errors[0].errors.map((error) => ({ message: error }))
      }

      if (externalErrors?.fault) {
        externalErrors.errors = [externalErrors.fault]
      }
    } catch (ex) {
      return ['Could not parse errors, invalid JSON in error message']
    }

    return externalErrors?.errors?.map((error) => error.message)
  }

  const allowPromote = (snapshot) => {
    if (!snapshot.external_status_updates) {
      return false
    }

    if (snapshot.external_status !== 'deployed') {
      return false
    }

    // pull statuses only for current snapshotId.
    // snapshotId would change if a "test" snapshot was "published"
    const statusesForCurrentSnapshotId = snapshot.external_status_updates
      .filter((update) => update.snapshotId === snapshot.external_snapshot_id)
      .map((update) => update.status)

    return statusesForCurrentSnapshotId.includes('published')
  }

  return (
    <div className='border-t mt-4 pt-4'>
      <div className='flex items-center justify-between border-b pb-4'>
        <div className='text-xl font-semibold'>Cycle Snapshots</div>
        <Button
          key={'generate-snapshot-button'}
          disabled={createModel.isLoading}
          icon={hasErrors ? 'info' : null}
          size='small'
          type={hasErrors ? 'error' : 'success'}
          onClick={() => handleClick()}
        >
          Generate new snapshot
        </Button>
      </div>
      <Message info small>
        Snapshots contain category and asset metadata for a specific point in time for a cycle. A new snapshot should be
        generated if metadata is updated. Images in snapshots are signed for 7 days.
      </Message>
      <Status pending={isLoading} error={error}>
        <Table
          widths={[200, 150, 140, 140, 200, 60]}
          header={{
            columns: [
              { text: 'Filename', key: 'filename' },
              { text: 'Created At', key: 'created_at' },
              { text: 'Status', key: 'status' },
              { text: 'External Status', key: 'external_status' },
              { text: '' },
              { text: '' },
            ],
          }}
          body={{
            data: snapshots,
            row: {
              compact: true,
              spaced: true,
              render: [
                (data) =>
                  !data.is_completed && !data.is_failed ? (
                    <Icon icon='autorenew' className='animate-spin-slow' />
                  ) : (
                    data?.filename
                  ),
                (data) => convertToDateTime(data?.created_at),
                (data) => (
                  <div className='flex space-x-2'>
                    <Tag
                      outline
                      type={data.is_completed ? 'success' : data.is_failed ? 'error' : 'warning'}
                      label={
                        data.is_completed
                          ? 'Complete'
                          : data.is_failed
                            ? 'Failed'
                            : `In Progress: ${parseInt(data.progress)}%`
                      }
                    />
                    {Boolean(data.generated_with_errors) && (
                      <Tag
                        outline
                        tooltip={{
                          text: `This snapshot was generated by ${data?.user?.email} despite errors being present.`,
                        }}
                        type={'error'}
                        label={'Generated with Errors'}
                      />
                    )}
                  </div>
                ),
                (data) =>
                  data.is_completed && (
                    <div className='flex items-center justify-between size-full'>
                      <Tag
                        outline
                        type={
                          data.external_status === null
                            ? 'warning'
                            : ['deployment-failed', 'failed', 'promote-failed'].includes(data.external_status)
                              ? 'error'
                              : 'success'
                        }
                        label={
                          data.external_status === null
                            ? 'Ready to Send'
                            : data.external_status.split('-').map(capitalize).join(' ')
                        }
                        tooltip={
                          data.external_status !== null && {
                            text: `This snapshot was sent by ${data?.submitted_by_user?.email}`,
                          }
                        }
                      />
                      {data.external_status !== null && (
                        <Button size='small' tertiary onClick={() => setDetailModalOpenForSnapshot(data)}>
                          Details
                        </Button>
                      )}
                    </div>
                  ),
                (data) => (
                  <div className='flex space-x-2'>
                    {data.external_status === null && (
                      <Button
                        size='small'
                        disabled={!data.is_completed}
                        onClick={() =>
                          sendSnapshotToViasat.mutate(
                            { snapshotID: data.id },
                            { onSuccess: () => queryClient.invalidateQueries(queryKey) }
                          )
                        }
                      >
                        Create Test Snapshot
                      </Button>
                    )}
                    {data.external_status === 'deployed' && !allowPromote(data) && (
                      <Button
                        size='small'
                        disabled={!data.is_completed}
                        onClick={() => sendSnapshotToViasat.mutate({ snapshotID: data.id, publish: true })}
                      >
                        Publish Test Snapshot
                      </Button>
                    )}
                    {allowPromote(data) && user?.has_unrestricted_edit_role && (
                      <Button size='small' onClick={() => promoteSnapshotToViasat.mutate({ snapshotID: data.id })}>
                        Promote
                      </Button>
                    )}
                  </div>
                ),
                (data) => (
                  <ButtonDropdown
                    kebab
                    dropdown={{
                      content: [
                        {
                          text: 'View snapshot',
                          disabled: !data.signed_url,
                          onClick: () => window.open(`${data.signed_url}`, '_blank'),
                        },
                        {
                          text: 'Delete',
                          className: 'text-red-500',
                          onClick: () => deleteSnapshot(data),
                        },
                      ],
                    }}
                  />
                ),
              ],
            },
          }}
          empty={{
            message: 'No snapshots available for this cycle.',
          }}
        />
      </Status>
      {modalOpen && (
        <Modal
          header={`Override Release Block`}
          onClose={() => setModalOpen(false)}
          confirmButton={{
            text: `Override and Generate Snapshot`,
            onClick: () => {
              setModalOpen(false)
              generateSnapshot()
            },
          }}
        >
          <p>
            {
              "You're about to generate a snapshot despite errors being present in the cycle. Proceeding will add a note to the snapshot with your user info."
            }
          </p>
        </Modal>
      )}
      {detailModalOpenForSnapshot !== null && (
        <Modal header={`External API Details`} onClose={() => setDetailModalOpenForSnapshot(null)}>
          <div className='grid grid-cols-2 gap-y-2'>
            <div className='font-bold'>Current Snapshot ID</div>
            <div>{detailModalOpenForSnapshot.external_snapshot_id}</div>
            <div className='font-bold'>Submitted By</div>
            <div>{detailModalOpenForSnapshot.submitted_by_user?.email ?? 'unknown'}</div>
            <div className='font-bold'>Filename</div>
            <div>{detailModalOpenForSnapshot.filename}</div>
          </div>

          <div className='mt-6 mb-2 text-lg font-bold'>Status Updates</div>
          <div className='grid grid-cols-[120px_220px_300px] gap-x-4 gap-y-2'>
            <div className='font-bold'>Status</div>
            <div className='font-bold'>Snapshot ID</div>
            <div className='font-bold'>Timestamp</div>
            {detailModalOpenForSnapshot.external_status_updates &&
              detailModalOpenForSnapshot.external_status_updates.map((update, index) => (
                <Fragment key={`${update.timestamp}-${update.status}-${index}`}>
                  <div className='flex gap-x-4'>
                    <Tag
                      outline
                      type={
                        ['deployment-failed', 'failed', 'promote-failed'].includes(update.status) ? 'error' : 'success'
                      }
                      label={update.status.split('-').map(capitalize).join(' ')}
                    />
                    {update.url && (
                      <a className='text-blue-600 underline' href={update.url} target='_blank'>
                        visit
                      </a>
                    )}
                  </div>

                  <div>{update.snapshotId}</div>
                  <div>{convertToDateTime(update.timestamp)}</div>
                </Fragment>
              ))}
          </div>

          {detailModalOpenForSnapshot.external_error && (
            <>
              <div className='my-4 font-bold'>Errors returned by the API</div>
              <ul role='list' className='list-decimal'>
                {extractExternalErrors(detailModalOpenForSnapshot)?.map((line, index) => (
                  <li key={`${line}-${index}`} className='mx-10'>
                    {line}
                  </li>
                ))}
              </ul>
              <div>
                <p className='my-4 font-bold'>Raw JSON</p>
                <InputArea disabled value={detailModalOpenForSnapshot.external_error ?? '<empty json>'} />
              </div>
            </>
          )}
        </Modal>
      )}
    </div>
  )
}
